/**
 * Security Utilities
 * Comprehensive security functions for input validation, sanitization, and protection
 */

import { getEncryptionKey } from './encryptionKeyManager';

/**
 * Content Security Policy configuration
 */
export const CSP_DIRECTIVES = {
  defaultSrc: ["'self'"],
  scriptSrc: ["'self'", "'unsafe-inline'", "'unsafe-eval'"], // Note: Remove unsafe-* in production
  styleSrc: ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com'],
  fontSrc: ["'self'", 'https://fonts.gstatic.com'],
  imgSrc: ["'self'", 'data:', 'https:', 'blob:'],
  connectSrc: [
    "'self'",
    import.meta.env['VITE_AUTH_API_URL'] || 'https://authentication.domainboost.io',
  ],
  frameSrc: ["'none'"],
  objectSrc: ["'none'"],
  upgradeInsecureRequests: [],
};

/**
 * Security Headers configuration
 */
export const SECURITY_HEADERS = {
  'X-Frame-Options': 'DENY',
  'X-Content-Type-Options': 'nosniff',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
};

/**
 * Sanitize HTML input to prevent XSS attacks
 */
export const sanitizeHTML = (input: string): string => {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
};

/**
 * Sanitize user input by removing dangerous characters
 */
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }

  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Validate URL format and ensure it's safe
 */
export const isValidURL = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

/**
 * Validate phone number (basic format)
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
  return phoneRegex.test(phone);
};

/**
 * Strong password validation
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */
export const isStrongPassword = (password: string): boolean => {
  if (password.length < 8) {
    return false;
  }

  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

  return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
};

/**
 * Get password strength score (0-4)
 */
export const getPasswordStrength = (
  password: string
): {
  score: number;
  feedback: string;
} => {
  let score = 0;
  const feedback: string[] = [];

  if (password.length >= 8) {
    score++;
  } else {
    feedback.push('At least 8 characters');
  }

  if (/[A-Z]/.test(password)) {
    score++;
  } else {
    feedback.push('One uppercase letter');
  }

  if (/[a-z]/.test(password)) {
    score++;
  } else {
    feedback.push('One lowercase letter');
  }

  if (/[0-9]/.test(password)) {
    score++;
  } else {
    feedback.push('One number');
  }

  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    score++;
  } else {
    feedback.push('One special character');
  }

  return {
    score: Math.min(score, 4),
    feedback: score >= 5 ? 'Strong password!' : `Missing: ${feedback.join(', ')}`,
  };
};

/**
 * Escape special characters for SQL-like queries (if needed)
 */
export const escapeSQLInput = (input: string): string => {
  // eslint-disable-next-line no-control-regex
  return input.replace(/[\0\x08\x09\x1a\n\r"'\\%]/g, (char) => {
    switch (char) {
      case '\0':
        return '\\0';
      case '\x08':
        return '\\b';
      case '\x09':
        return '\\t';
      case '\x1a':
        return '\\z';
      case '\n':
        return '\\n';
      case '\r':
        return '\\r';
      case '"':
      case "'":
      case '\\':
      case '%':
        return `\\${char}`;
      default:
        return char;
    }
  });
};

/**
 * Sanitize object by removing undefined/null values and sanitizing strings
 */
export const sanitizeObject = <T extends Record<string, unknown>>(obj: T): T => {
  const sanitized = {} as T;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (value === null || value === undefined) {
        continue;
      }

      if (typeof value === 'string') {
        sanitized[key] = sanitizeInput(value) as T[Extract<keyof T, string>];
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        sanitized[key] = sanitizeObject(value as Record<string, unknown>) as T[Extract<
          keyof T,
          string
        >];
      } else {
        sanitized[key] = value as T[Extract<keyof T, string>];
      }
    }
  }

  return sanitized;
};

/**
 * Rate limiter for client-side requests
 */
export class ClientRateLimiter {
  private requests: Map<string, number[]> = new Map();
  private maxRequests: number;
  private windowMs: number;

  constructor(maxRequests: number = 100, windowMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  /**
   * Check if request is allowed
   */
  isAllowed(key: string): boolean {
    const now = Date.now();
    const timestamps = this.requests.get(key) ?? [];

    // Remove old timestamps outside the window
    const validTimestamps = timestamps.filter((t) => now - t < this.windowMs);

    if (validTimestamps.length >= this.maxRequests) {
      return false;
    }

    validTimestamps.push(now);
    this.requests.set(key, validTimestamps);

    return true;
  }

  /**
   * Get remaining requests
   */
  getRemaining(key: string): number {
    const now = Date.now();
    const timestamps = this.requests.get(key) ?? [];
    const validTimestamps = timestamps.filter((t) => now - t < this.windowMs);

    return Math.max(0, this.maxRequests - validTimestamps.length);
  }

  /**
   * Clear rate limit for a key
   */
  clear(key: string): void {
    this.requests.delete(key);
  }

  /**
   * Clear all rate limits
   */
  clearAll(): void {
    this.requests.clear();
  }
}

/**
 * CSRF Token Manager
 */
export class CSRFTokenManager {
  private static TOKEN_KEY = 'csrf_token';
  private static token: string | null = null;

  /**
   * Generate a new CSRF token
   */
  static generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const token = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    this.token = token;
    sessionStorage.setItem(this.TOKEN_KEY, token);
    return token;
  }

  /**
   * Get current CSRF token
   */
  static getToken(): string {
    if (this.token) {
      return this.token;
    }

    const stored = sessionStorage.getItem(this.TOKEN_KEY);
    if (stored) {
      this.token = stored;
      return stored;
    }

    return this.generateToken();
  }

  /**
   * Validate CSRF token
   */
  static validateToken(token: string): boolean {
    const currentToken = this.getToken();
    return token === currentToken;
  }

  /**
   * Clear CSRF token
   */
  static clearToken(): void {
    this.token = null;
    sessionStorage.removeItem(this.TOKEN_KEY);
  }
}

/**
 * Secure storage wrapper with AES encryption
 * Uses Web Crypto API for strong encryption
 */
export class SecureStorage {
  private static PREFIX = '__secure__';
  private static ENCRYPTION_KEY = getEncryptionKey();

  /**
   * AES-256-GCM encryption using Web Crypto API
   * More secure than basic XOR encryption
   */
  private static async encrypt(data: string): Promise<string> {
    try {
      // Convert key to crypto key
      const encoder = new TextEncoder();
      const keyMaterial = encoder.encode(this.ENCRYPTION_KEY);

      // Hash the key to get 256 bits
      const keyHash = await crypto.subtle.digest('SHA-256', keyMaterial);
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyHash,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
      );

      // Generate random IV
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt the data
      const encodedData = encoder.encode(data);
      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encodedData
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);

      // Convert to base64
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption error:', error);
      // Fallback to basic encoding if crypto fails
      return btoa(data);
    }
  }

  /**
   * AES-256-GCM decryption using Web Crypto API
   */
  private static async decrypt(encryptedData: string): Promise<string> {
    try {
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData)
          .split('')
          .map((c) => c.charCodeAt(0))
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      // Convert key to crypto key
      const encoder = new TextEncoder();
      const keyMaterial = encoder.encode(this.ENCRYPTION_KEY);

      const keyHash = await crypto.subtle.digest('SHA-256', keyMaterial);
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyHash,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );

      // Decrypt
      const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, data);

      // Convert back to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedData);
    } catch (error) {
      console.error('Decryption error:', error);
      // Fallback to basic decoding if crypto fails
      try {
        return atob(encryptedData);
      } catch {
        return '';
      }
    }
  }

  /**
   * Set secure item (async for encryption)
   */
  static async setItem(key: string, value: string): Promise<void> {
    const encrypted = await this.encrypt(value);
    localStorage.setItem(this.PREFIX + key, encrypted);
  }

  /**
   * Get secure item (async for decryption)
   */
  static async getItem(key: string): Promise<string | null> {
    const encrypted = localStorage.getItem(this.PREFIX + key);
    if (!encrypted) {
      return null;
    }

    return this.decrypt(encrypted);
  }

  /**
   * Remove secure item
   */
  static removeItem(key: string): void {
    localStorage.removeItem(this.PREFIX + key);
  }

  /**
   * Clear all secure items
   */
  static clear(): void {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(this.PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }
}

/**
 * Detect potential XSS attempts
 */
export const detectXSS = (input: string): boolean => {
  const xssPatterns = [
    /<script[^>]*>[\s\S]*?<\/script>/gi,
    /<iframe[^>]*>[\s\S]*?<\/iframe>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<img[^>]*onerror=/gi,
    /<svg[^>]*onload=/gi,
    /eval\(/gi,
    /expression\(/gi,
  ];

  return xssPatterns.some((pattern) => pattern.test(input));
};

/**
 * Validate and sanitize form data
 */
export const validateFormData = (
  data: Record<string, unknown>
): {
  isValid: boolean;
  errors: Record<string, string>;
  sanitized: Record<string, unknown>;
} => {
  const errors: Record<string, string> = {};
  const sanitized: Record<string, unknown> = {};

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];

      if (typeof value === 'string') {
        // Check for XSS
        if (detectXSS(value)) {
          errors[key] = 'Invalid input detected';
          continue;
        }

        // Sanitize
        sanitized[key] = sanitizeInput(value);
      } else {
        sanitized[key] = value;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitized,
  };
};

/**
 * Generate secure random string
 */
export const generateSecureRandomString = (length: number = 32): string => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Hash string (simple, for client-side)
 * Note: Not cryptographically secure, for production use backend hashing
 */
export const simpleHash = async (input: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Prevent clickjacking
 */
export const preventClickjacking = (): void => {
  if (window.top !== window.self) {
    window.top!.location.href = window.self.location.href;
  }
};

/**
 * Check if running in secure context (HTTPS)
 */
export const isSecureContext = (): boolean => {
  return window.isSecureContext || window.location.protocol === 'https:';
};

/**
 * Security event logger
 */
export class SecurityLogger {
  private static logs: Array<{
    timestamp: Date;
    event: string;
    details: unknown;
  }> = [];

  static log(event: string, details?: unknown): void {
    this.logs.push({
      timestamp: new Date(),
      event,
      details: details || {},
    });

    // In production, send to backend
    console.warn(`[SECURITY] ${event}`, details);
  }

  static getLogs(): Array<{
    timestamp: Date;
    event: string;
    details: unknown;
  }> {
    return [...this.logs];
  }

  static clearLogs(): void {
    this.logs = [];
  }
}

// Initialize clickjacking protection
if (typeof window !== 'undefined') {
  preventClickjacking();
}
